<template>
    <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form id="locale-string-form"
                autocomplete="new-password"
                @submit.stop.prevent="handleSubmit(saveEditData)"
                @reset="resetForm"
        >
            <dl class="description-list-wrapper">
                <DescriptionItem :label="$t('문자열 키')" mode="form" labelFor="edit-form-locale-key"
                                 required
                >
                    <div class="w-100">
                        <validation-provider
                            v-slot="validationContext"
                            name="edit-form-locale-key"
                            :rules="{ required: true, min: 2 }"
                        >
                            <b-form-input id="edit-form-locale-key"
                                          v-model="form.key"
                                          name="edit-form-locale-key"
                                          :disabled="editLoading || mode === 'edit'"
                                          :placeholder="$t('문자열 키')"
                                          :state="getValidationState(validationContext)"
                                          autocomplete="new-password"
                                          aria-describedby="edit-form-locale-key-feedback"
                            />
                            <b-form-invalid-feedback id="edit-form-locale-key-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>
                <DescriptionItem :label="$t('한국어')" mode="form" labelFor="edit-form-ko"
                                 required
                >
                    <div class="w-100">
                        <validation-provider
                            v-slot="validationContext"
                            name="edit-form-ko"
                            :rules="{ required: true }"
                        >
                            <b-form-input id="edit-form-ko"
                                          v-model="form.ko"
                                          name="edit-form-ko"
                                          :disabled="editLoading"
                                          :placeholder="$t('한국어')"
                                          :state="getValidationState(validationContext)"
                                          autocomplete="new-password"
                                          aria-describedby="edit-form-ko-feedback"
                            />
                            <b-form-invalid-feedback id="edit-form-ko-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('영어')" mode="form" labelFor="edit-form-en">
                    <div class="w-100">
                        <b-form-input id="edit-form-en"
                                      v-model="form.en"
                                      name="edit-form-en"
                                      :disabled="editLoading"
                                      :placeholder="$t('영어')"
                                      autocomplete="new-password"
                        />
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('중국어')" mode="form" labelFor="edit-form-ca">
                    <div class="w-100">
                        <b-form-input id="edit-form-ca"
                                      v-model="form.ca"
                                      name="edit-form-ca"
                                      :disabled="editLoading"
                                      :placeholder="$t('중국어')"
                                      autocomplete="new-password"
                        />
                    </div>
                </DescriptionItem>

                <div v-if="mode === 'edit'" class="row-wrapper">
                    <DescriptionItem :label="$t('등록일시')">
                        {{ get_date(detailData.created_at) || '-' }}
                    </DescriptionItem>

                    <DescriptionItem :label="$t('수정일시')">
                        {{ get_date(detailData.updated_at) || '-' }}
                    </DescriptionItem>
                </div>
            </dl>
        </b-form>
    </validation-observer>
</template>

<script>
import {createLocalizedString, updateLocalizedString} from '@shared_utils/api/projectSchema';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import DescriptionItem from '@shared/components/common/DescriptionItem.vue';
import {getDate} from '@shared/components/admin/pages/Management/common-data';

// 미저장 판정 대상 (저장 시 보내는 입력 칸)
const EDITABLE_KEYS = ['key', 'ko', 'en', 'ca'];

export default {
    name: 'LocaleStringForm',
    components: {
        DescriptionItem,
    },
    props: {
        detailData: {
            type: [Object, null],
            default: null,
        },
        mode: {
            type: String,
            required: true,
            default: 'add', // add | edit
        }
    },

    data() {
        return {
            editLoading: false,
            form: {
                key: null,
                ko: null,
                en: null,
                ca: null,
            },
            savedSnapshot: null,
        };
    },
    computed: {
        // 부모 모달이 닫기 전 미저장 확인에 쓴다 (@dirty)
        dirty() {
            return this.savedSnapshot != null && this.snapshot() !== this.savedSnapshot;
        },
        get_date() {
            return (date) => {
                return getDate(date);
            };
        }
    },
    watch: {
        detailData: {
            immediate: true,
            handler() {
                this.form = {...this.form, ...this.detailData};
                this.savedSnapshot = this.snapshot();
            }
        },
        dirty(value) {
            this.$emit('dirty', value);
        },
    },
    beforeDestroy() {
        this.$emit('dirty', false);
    },
    methods: {
        snapshot() {
            return JSON.stringify(EDITABLE_KEYS.map((key) => this.form[key] ?? ''));
        },
        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        async saveEditData() {
            if (this.editLoading) return;
            try {
                this.editLoading = true;

                let response = null;
                const form = {...this.form};
                if (this.mode === 'add') {
                    response = await createLocalizedString(form);
                } else if (this.mode === 'edit') {
                    response = await updateLocalizedString(this.detailData.key, form);
                }

                this.$EventBus.$emit('toast-success', this.$t('다국어 문자열 등록 및 수정'), this.$t('다국어 문자열이 성공적으로 저장되었습니다.'));
                this.$emit('updateDetailData', {...this.detailData, ...this.form, ...response.data});
            } catch (error) {
                console.error(error);
                const status = error?.response?.status;
                if (status === 401) return;
                // 입력 문제(4xx, 예: 중복 키 409)는 서버 사유를 그대로 보여 관리자가 직접 고치게 한다
                const reason = status >= 400 && status < 500 ? getErrorMessage(error, '') : '';
                this.$EventBus.$emit('toast-failure', this.$t('다국어 문자열 등록 및 수정'), reason || '다국어 문자열 데이터 저장 도중 오류가 발생했습니다. 개발 팀에 문의해주세요.');
            } finally {
                this.editLoading = false;
            }
        },
        resetForm() {
            this.form = {
                key: null,
                ko: null,
                en: null,
                ca: null,
            };
        },
    },
};
</script>

<style scoped lang="scss">

</style>
